import React from 'react';
import { CheckCircleIcon as CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface PasswordValidationProps {
  password: string;
  showValidation?: boolean;
}

interface ValidationRule {
  test: (password: string) => boolean;
  message: string;
}

const validationRules: ValidationRule[] = [
  {
    test: (password: string) => password.length >= 8,
    message: 'En az 8 karakter olmalı'
  },
  {
    test: (password: string) => /[A-Z]/.test(password),
    message: 'En az 1 büyük harf içermeli'
  },
  {
    test: (password: string) => /[a-z]/.test(password),
    message: 'En az 1 küçük harf içermeli'
  },
  {
    test: (password: string) => /\d/.test(password),
    message: 'En az 1 rakam içermeli'
  },
  {
    test: (password: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    message: 'En az 1 özel karakter içermeli (!@#$%^&* vb.)'
  },
  {
    test: (password: string) => {
      const commonPasswords = [
        '123456', 'password', '123456789', '12345678', '12345',
        '1234567', 'password123', 'admin', 'qwerty', 'abc123',
        'Password1', '123123', 'welcome', 'login', 'admin123'
      ];
      return !commonPasswords.some(common => 
        password.toLowerCase().includes(common.toLowerCase())
      );
    },
    message: 'Yaygın şifreler kullanılamaz'
  }
];

export const validatePassword = (password: string): { isValid: boolean; score: number; feedback: string[] } => {
  const passedRules = validationRules.filter(rule => rule.test(password));
  const failedRules = validationRules.filter(rule => !rule.test(password));
  
  const score = Math.round((passedRules.length / validationRules.length) * 100);
  const isValid = failedRules.length === 0;
  const feedback = failedRules.map(rule => rule.message);

  return { isValid, score, feedback };
};

export const PasswordValidation: React.FC<PasswordValidationProps> = ({ 
  password, 
  showValidation = true 
}) => {
  if (!showValidation || !password) return null;

  const { score, feedback } = validatePassword(password);

  const getStrengthColor = (score: number) => {
    if (score < 30) return 'bg-red-500';
    if (score < 60) return 'bg-yellow-500';
    if (score < 80) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthText = (score: number) => {
    if (score < 30) return 'Zayıf';
    if (score < 60) return 'Orta';
    if (score < 80) return 'İyi';
    return 'Güçlü';
  };

  return (
    <div className="mt-2 space-y-2">
      {/* Şifre Gücü Göstergesi */}
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Şifre Gücü</span>
          <span className={`font-medium ${
            score < 30 ? 'text-red-600' : 
            score < 60 ? 'text-yellow-600' : 
            score < 80 ? 'text-blue-600' : 'text-green-600'
          }`}>
            {getStrengthText(score)} ({score}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(score)}`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {/* Kurallar Listesi */}
      <div className="space-y-1">
        {validationRules.map((rule, index) => {
          const isPassed = rule.test(password);
          return (
            <div key={index} className="flex items-center space-x-2 text-sm">
              {isPassed ? (
                <CheckIcon className="h-4 w-4 text-green-500" />
              ) : (
                <XMarkIcon className="h-4 w-4 text-red-500" />
              )}
              <span className={isPassed ? 'text-green-600' : 'text-red-600'}>
                {rule.message}
              </span>
            </div>
          );
        })}
      </div>

      {/* Ek Güvenlik Uyarıları */}
      {password.length > 0 && feedback.length === 0 && (
        <div className="p-2 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-700 flex items-center">
            <CheckIcon className="h-4 w-4 mr-1" />
            Şifreniz güvenlik gereksinimlerini karşılıyor!
          </p>
        </div>
      )}
    </div>
  );
};

export default PasswordValidation;